class QuantityInputSelector extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-quantity-input]');
    if (!this.input) return;

    if (!this.input.getAttribute('min')) this.input.setAttribute('min', this.dataset.min || '1');
    if (!this.input.getAttribute('step')) this.input.setAttribute('step', this.dataset.step || '1');
    if (!this.input.getAttribute('name')) this.input.setAttribute('name', 'quantity');

    this.btnInc = this.querySelector('.btn-increase');
    this.btnDec = this.querySelector('.btn-decrease');

    // init
    this.setValue(this.input.value, { emit: false });

    // events
    this.addEventListener('click', this.onClick);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeyDown);

    // listen variant change
    this.root = this.closest('[data-product-root]') || this.closest('.shopify-section') || document;
    this.root.addEventListener('variant:change', this.onVariantChange);

    this._syncUI();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKeyDown);
    }
    this.root?.removeEventListener('variant:change', this.onVariantChange);
  }

  /* ---------------- getters ---------------- */

  get min() {
    const v = parseInt(this.input?.getAttribute('min') || this.dataset.min || '1', 10);
    return Number.isFinite(v) ? v : 1;
  }

  get step() {
    const v = parseInt(this.input?.getAttribute('step') || this.dataset.step || '1', 10);
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  get max() {
    const raw = this.input?.getAttribute('max');
    if (raw === null || raw === undefined || raw === '') return null;
    const v = parseInt(raw, 10);
    return Number.isFinite(v) ? v : null;
  }

  get value() {
    const v = parseInt(this.input?.value || String(this.min), 10);
    return Number.isFinite(v) ? v : this.min;
  }

  /* ---------------- variant change ---------------- */

  onVariantChange(e) {
    const variant = e.detail?.variant;
    if (!variant) return;

    // ✅ ưu tiên qty từ picker sync (map)
    const invFromDetail = e.detail?.inventory_quantity;
    const invFromVariant = variant.inventory_quantity;

    const invQty =
      Number.isFinite(Number(invFromDetail)) ? Number(invFromDetail) :
      (typeof invFromVariant !== 'undefined' && invFromVariant !== null) ? Number(invFromVariant) :
      null;

    // sold out => disable
    const available = variant.available !== false;

    if (!available || invQty !== null && invQty <= 0) {
      this.input.setAttribute('max', '0');
      this._setDisabled(true);
      this.setValue(this.min, { emit: true });
      return;
    }

    // Nếu có inventory qty => set max
    if (invQty !== null) {
      this.input.setAttribute('max', String(Math.max(0, Math.floor(invQty))));
    } else {
      // Không có thông tin tồn kho => bỏ max (không giới hạn)
      this.input.removeAttribute('max');
    }

    this._setDisabled(false);

    // clamp lại theo max mới
    this.setValue(this.value, { emit: true });
  }

  /* ---------------- interactions ---------------- */

  onClick(e) {
    const btn = e.target.closest('button');
    if (!btn || !this.contains(btn)) return;
    if (this.input.disabled) return;

    if (btn.classList.contains('btn-increase')) {
      e.preventDefault();
      this.setValue(this.value + this.step);
      return;
    }

    if (btn.classList.contains('btn-decrease')) {
      e.preventDefault();
      this.setValue(this.value - this.step);
    }
  }

  onInput() {
    if (this.input.disabled) return;
    this.setValue(this.input.value);
  }

  onKeyDown(e) {
    if (this.input.disabled) return;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setValue(this.value + this.step);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setValue(this.value - this.step);
    }
  }

  /* ---------------- value helpers ---------------- */

  clamp(next) {
    let v = parseInt(next, 10);
    if (!Number.isFinite(v)) v = this.min;

    v = Math.max(this.min, v);

    const mx = this.max;
    if (mx !== null) v = Math.min(mx, v);

    return v;
  }

  setValue(next, { emit = true } = {}) {
    const prev = this.value;
    const v = this.clamp(next);

    this.input.value = String(v);
    this._syncUI();

    if (!emit) return;
    if (v === prev) return;

    // native events
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));

    // custom event
    this.dispatchEvent(new CustomEvent('quantity:change', {
      bubbles: true,
      detail: { quantity: v }
    }));
  }

  /* ---------------- UI sync ---------------- */

  _setDisabled(disabled) {
    this.input.disabled = !!disabled;
    this._syncUI();
  }

  _syncUI() {
    const disabled = !!this.input.disabled;
    const atMin = this.value <= this.min;
    const atMax = this.max !== null && this.value >= this.max;

    if (this.btnInc) {
      this.btnInc.disabled = disabled || atMax;
      this.btnInc.classList.toggle('disabled', disabled || atMax);
    }
    if (this.btnDec) {
      this.btnDec.disabled = disabled || atMin;
      this.btnDec.classList.toggle('disabled', disabled || atMin);
    }

    this.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }
}

if (!customElements.get('quantity-input-selector')) {
  customElements.define('quantity-input-selector', QuantityInputSelector);
}
